/*
 * Copyright (c) 2026 Paxia AI Pty Ltd. All rights reserved.
 * This software is proprietary and confidential.
 * No part of this codebase may be copied, modified, published, distributed, sublicensed, or used without prior written permission from Paxia AI Pty Ltd.
 */

/**
 * Expert Editor Panel Component
 * A reusable panel for viewing and editing expert configuration
 * Used in both AISystemCanvas and AIBuilderInsightsPanel
 */

import React, { useState, useEffect, useRef, useCallback } from 'react';
import { useMutation, useQueryClient, useQuery } from '@tanstack/react-query';
import {
  Paper,
  Box,
  Typography,
  IconButton,
  Divider,
  Stack,
  Button,
  CircularProgress,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  useMediaQuery,
  useTheme,
} from '@mui/material';
import {
  Delete as DeleteIcon,
  Close as CloseIcon,
  Save as SaveIcon,
  PlayArrow as PlayArrowIcon,
  Fullscreen as FullscreenIcon,
  FullscreenExit as FullscreenExitIcon,
} from '@mui/icons-material';
import { Node, Edge } from '@xyflow/react';
import { toast } from 'react-toastify';

import { expertApi } from '../../services/api';
import type { Expert, ExpertListItem, ExpertCreate } from '../../types';
import { ExpertEditForm } from '../expert/ExpertEditForm';
import { TestExpertDialog } from '../expert/TestExpertDialog';

export interface ExpertEditorPanelProps {
  selectedNode: Node;
  edges: Edge[];
  expertsData: any; // Expert list data from API
  onClose: () => void;
  onUpdate: (expert: Expert | null) => void;
  compact?: boolean; // Whether to use compact styling for embedded mode
  startInEditMode?: boolean;
}

const ExpertEditorPanel: React.FC<ExpertEditorPanelProps> = ({ 
  selectedNode, 
  edges = [],
  expertsData, 
  onClose, 
  onUpdate,
  compact = false,
  startInEditMode = false,
}) => {
  const [isFullscreen, setIsFullscreen] = useState(false);
  const [editFormData, setEditFormData] = useState<Partial<ExpertCreate>>({});
  const [deleteConfirmOpen, setDeleteConfirmOpen] = useState(false);
  const [testDialogOpen, setTestDialogOpen] = useState(false);
  
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));

  // Auto-enable fullscreen on mobile
  useEffect(() => {
    if (isMobile && !isFullscreen) {
      setIsFullscreen(true);
    }
  }, [isMobile]);

  const queryClient = useQueryClient();

  // Get the expert from the node data
  const expertListItem = (selectedNode.data as any)?.expert as ExpertListItem;
  const expertId = expertListItem?.id;

  // Fetch full expert details immediately so selecting a node opens the editor directly.
  const { data: fullExpert, isLoading: isLoadingExpert } = useQuery({
    queryKey: ['expert', expertId],
    queryFn: () => expertApi.get(expertId),
    enabled: !!expertId,
    staleTime: 0, // Always fetch fresh data when query is enabled
    refetchOnMount: 'always', // Always refetch when component mounts
  });

  // Update mutation
  const updateExpertMutation = useMutation({
    mutationFn: (data: Partial<ExpertCreate>) => {
      console.log('📤 [EXPERT EDITOR PANEL] UPDATE MUTATION - Sending data:', data);
      return expertApi.update(expertId, data);
    },
    onSuccess: (updatedExpert) => {
      console.log('✅ [EXPERT EDITOR PANEL] UPDATE SUCCESS - Response:', updatedExpert);
      // Invalidate both the list and the specific expert query
      queryClient.invalidateQueries({ queryKey: ['experts'] });
      queryClient.invalidateQueries({ queryKey: ['expert', expertId] });
      toast.success('Expert updated successfully');
      onUpdate(updatedExpert);
    },
    onError: (error: any) => {
      console.error('❌ [EXPERT EDITOR PANEL] UPDATE ERROR:', error);
      toast.error(error?.response?.data?.detail || 'Failed to update expert');
    },
  });

  // Delete mutation
  const deleteExpertMutation = useMutation({
    mutationFn: () => expertApi.delete(expertId),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['experts'] });
      toast.success('Expert deleted successfully');
      onClose(); // Close panel
      // Remove node from canvas
      onUpdate(null); // Signal deletion to parent
    },
    onError: (error: any) => {
      toast.error(error?.response?.data?.detail || 'Failed to delete expert');
    },
  });

  // Initialize form data when full expert is loaded
  useEffect(() => {
    if (fullExpert) {
      setEditFormData({
        name: fullExpert.name,
        expert_type: fullExpert.expert_type,
        model: fullExpert.model,
        ai_system: fullExpert.ai_system,
        agent: fullExpert.agent,
        system_prompt: fullExpert.system_prompt || '',
        prompt: fullExpert.prompt || '',
        code: fullExpert.code || '', // Load code field for CODE experts
        retry: fullExpert.retry || 0,
        use_cache: fullExpert.use_cache || false,
        cache_ttl_seconds: fullExpert.cache_ttl_seconds ?? 3600,
        cache_max_uses: fullExpert.cache_max_uses ?? null,
        dry_run: fullExpert.dry_run || false,
        is_active: fullExpert.is_active !== false,
        input_data: fullExpert.input_data || { inputs: [] },
        output: fullExpert.output || {},
        // Evaluator fields
        use_evaluator: fullExpert.use_evaluator ?? false,
        evaluator_model: fullExpert.evaluator_model ?? null,
        evaluator_system_prompt: fullExpert.evaluator_system_prompt ?? '',
        evaluator_accuracy_threshold: fullExpert.evaluator_accuracy_threshold ?? 70,
        evaluator_hallucination_threshold: fullExpert.evaluator_hallucination_threshold ?? 30,
        evaluator_on_failure: fullExpert.evaluator_on_failure ?? 'continue',
        evaluator_on_low_accuracy: fullExpert.evaluator_on_low_accuracy ?? 'stop',
        evaluator_on_high_hallucination: fullExpert.evaluator_on_high_hallucination ?? 'stop',
        evaluator_max_retries: fullExpert.evaluator_max_retries ?? 2,
        // Execution conditions
        execution_conditions: fullExpert.execution_conditions ?? {},
      });
    }
  }, [fullExpert]);

  const handleSave = () => {
    // Validate required fields
    if (!editFormData.name?.trim()) {
      toast.error('Expert name is required');
      return;
    }
    
    // For AGENT type, require agent selection instead of model
    if (editFormData.expert_type === 'AGENT') {
      if (!editFormData.agent) {
        toast.error('Agent is required for AGENT type expert');
        return;
      }
    } else if (editFormData.expert_type === 'CODE') {
      // CODE type doesn't need a model - it executes Python code directly
      if (!editFormData.code || !editFormData.code.trim()) {
        toast.error('Python code is required for CODE type expert');
        return;
      }
    } else {
      // For ANALYSIS/ACTION types, model is required
      if (!editFormData.model) {
        toast.error('Model is required');
        return;
      }
    }
    
    // Prepare data for submission - ensure model is null for AGENT and CODE types
    const submitData = {
      ...editFormData,
      model: (editFormData.expert_type === 'AGENT' || editFormData.expert_type === 'CODE') ? null : editFormData.model,
      // When evaluator is disabled, reset action fields to valid defaults
      ...(!editFormData.use_evaluator ? {
        evaluator_on_failure: 'continue',
        evaluator_on_low_accuracy: 'stop',
        evaluator_on_high_hallucination: 'stop',
      } : {}),
      // When CODE type, force evaluator off
      ...(editFormData.expert_type === 'CODE' ? {
        use_evaluator: false,
        evaluator_on_failure: 'continue',
        evaluator_on_low_accuracy: 'stop',
        evaluator_on_high_hallucination: 'stop',
      } : {}),
    };
    
    updateExpertMutation.mutate(submitData);
  };

  const handleCancel = () => {
    setEditFormData({});
    onClose();
  };

  const handleDeleteClick = () => {
    setDeleteConfirmOpen(true);
  };

  const handleDeleteConfirm = () => {
    deleteExpertMutation.mutate();
    setDeleteConfirmOpen(false);
  };

  const handleDeleteCancel = () => {
    setDeleteConfirmOpen(false);
  };

  // Resizable panel width logic — use % of viewport width
  const DEFAULT_EDIT_WIDTH = Math.round(window.innerWidth * 0.45); // 45% of viewport
  const MIN_PANEL_WIDTH = 350;
  const MAX_PANEL_WIDTH = Infinity;

  const [resizableWidth, setResizableWidth] = useState(DEFAULT_EDIT_WIDTH);
  const isResizing = useRef(false);
  const startX = useRef(0);
  const startWidth = useRef(0);

  const handleResizeMouseDown = useCallback((e: React.MouseEvent) => {
    isResizing.current = true;
    startX.current = e.clientX;
    startWidth.current = resizableWidth;
    document.body.style.cursor = 'col-resize';
    document.body.style.userSelect = 'none';
    e.preventDefault();
  }, [resizableWidth]);

  useEffect(() => {
    const handleMouseMove = (e: MouseEvent) => {
      if (!isResizing.current) return;
      // Panel is on the right, so dragging left increases width
      const diff = startX.current - e.clientX;
      const newWidth = Math.max(MIN_PANEL_WIDTH, Math.min(MAX_PANEL_WIDTH, startWidth.current + diff));
      setResizableWidth(newWidth);
    };

    const handleMouseUp = () => {
      if (isResizing.current) {
        isResizing.current = false;
        document.body.style.cursor = '';
        document.body.style.userSelect = '';
      }
    };

    document.addEventListener('mousemove', handleMouseMove);
    document.addEventListener('mouseup', handleMouseUp);

    return () => {
      document.removeEventListener('mousemove', handleMouseMove);
      document.removeEventListener('mouseup', handleMouseUp);
    };
  }, []);

  if (!expertListItem) return null;

  // Determine panel width based on compact mode
  const panelWidth = compact ? '100%' : resizableWidth;

  const incomingCount = edges.filter((edge) => edge.target === selectedNode.id).length;
  const outgoingCount = edges.filter((edge) => edge.source === selectedNode.id).length;
  const expertTypeColor: 'primary' | 'warning' | 'info' | 'secondary' =
    expertListItem.expert_type === 'ANALYSIS'
      ? 'primary'
      : expertListItem.expert_type === 'CODE'
        ? 'warning'
        : expertListItem.expert_type === 'AGENT'
          ? 'info'
          : 'secondary';
  const detailSurfaceSx = {
    px: 1.25,
    py: 1,
    border: 1,
    borderColor: 'divider',
    borderRadius: 1.5,
    bgcolor: 'background.default',
  } as const;

  // Content that will be rendered in both Paper and Dialog modes
  const panelContent = (
    <>
      <Box display="flex" alignItems="center" justifyContent="space-between" mb={2}>
        <Typography variant="h6">
          Edit Expert
        </Typography>
        <Box display="flex" gap={0.5}>
          {expertId && (
            <IconButton
              size="small"
              onClick={() => setTestDialogOpen(true)}
              color="success"
              disabled={isLoadingExpert}
              aria-label="Test expert"
            >
              <PlayArrowIcon />
            </IconButton>
          )}
          <IconButton size="small" onClick={() => setIsFullscreen(!isFullscreen)}>
            {isFullscreen ? <FullscreenExitIcon /> : <FullscreenIcon />}
          </IconButton>
          <IconButton size="small" onClick={onClose}>
            <CloseIcon />
          </IconButton>
        </Box>
      </Box>

      <Divider sx={{ mb: 1.5 }} />

      {isLoadingExpert ? (
        <Box display="flex" justifyContent="center" py={4}>
          <CircularProgress />
        </Box>
      ) : (
        <Stack spacing={2.5}>
          <ExpertEditForm
            expert={fullExpert}
            formData={editFormData}
            onChange={(updates) => setEditFormData((prev) => ({ ...prev, ...updates }))}
            disabled={updateExpertMutation.isPending}
            hideSystemSelector={true}
            compact={compact || !isFullscreen || isMobile}
            promptMinHeight={isMobile ? 350 : (isFullscreen ? 550 : 450)}
          />

          <Box display="flex" gap={1} justifyContent="space-between" pt={1}>
            <Button
              color="error"
              onClick={handleDeleteClick}
              disabled={updateExpertMutation.isPending || deleteExpertMutation.isPending}
              startIcon={<DeleteIcon />}
            >
              Delete
            </Button>
            <Box display="flex" gap={1}>
              <Button onClick={handleCancel} disabled={updateExpertMutation.isPending}>
                Cancel
              </Button>
              <Button
                variant="contained"
                onClick={handleSave}
                disabled={updateExpertMutation.isPending}
                startIcon={updateExpertMutation.isPending ? <CircularProgress size={16} /> : <SaveIcon />}
              >
                {updateExpertMutation.isPending ? 'Saving...' : 'Save'}
              </Button>
            </Box>
          </Box>
        </Stack>
      )}
    </>
  );

  // Render as fullscreen Dialog or side Panel
  if (isFullscreen) {
    return (
      <>
        <Dialog
          open={true}
          onClose={() => setIsFullscreen(false)}
          maxWidth={false}
          fullWidth
          fullScreen
        >
          <DialogContent sx={{ p: { xs: 2, sm: 2.5 } }}>
            {panelContent}
          </DialogContent>
        </Dialog>
        
        {/* Test Expert Dialog - rendered outside fullscreen dialog */}
        {fullExpert && (
          <TestExpertDialog
            open={testDialogOpen}
            onClose={() => setTestDialogOpen(false)}
            expert={fullExpert}
          />
        )}
      </>
    );
  }

  // Default: Render as panel (Paper for standalone, Box for compact/embedded)
  if (compact) {
    return (
      <>
        <Paper elevation={0} sx={{ p: 1.75, border: 1, borderColor: 'divider', borderRadius: 2, bgcolor: 'background.paper' }}>
          {panelContent}
        </Paper>
        
        {/* Delete Confirmation Dialog */}
        <Dialog open={deleteConfirmOpen} onClose={handleDeleteCancel}>
          <DialogTitle>Delete Expert</DialogTitle>
          <DialogContent>
            <Typography>
              Are you sure you want to delete the expert <strong>{expertListItem?.name}</strong>? This action cannot be undone.
            </Typography>
          </DialogContent>
          <DialogActions>
            <Button onClick={handleDeleteCancel}>Cancel</Button>
            <Button
              onClick={handleDeleteConfirm}
              color="error"
              variant="contained"
              disabled={deleteExpertMutation.isPending}
              startIcon={deleteExpertMutation.isPending ? <CircularProgress size={16} /> : <DeleteIcon />}
            >
              {deleteExpertMutation.isPending ? 'Deleting...' : 'Delete'}
            </Button>
          </DialogActions>
        </Dialog>
        
        {/* Test Expert Dialog */}
        {fullExpert && (
          <TestExpertDialog
            open={testDialogOpen}
            onClose={() => setTestDialogOpen(false)}
            expert={fullExpert}
          />
        )}
      </>
    );
  }

  return (
    <>
      <Paper sx={{ 
        width: panelWidth, 
        px: 2,
        py: 1.75,
        overflow: 'auto', 
        maxHeight: '100%', 
        position: 'relative',
        flexShrink: 0,
        borderLeft: 1,
        borderColor: 'divider',
        borderRadius: 0,
        boxShadow: 'none',
        bgcolor: 'background.paper',
        transition: isResizing.current ? 'none' : 'width 0.2s',
      }}>
        {/* Resize drag handle on left edge */}
        <Box
          onMouseDown={handleResizeMouseDown}
          sx={{
            position: 'absolute',
            left: 0,
            top: 0,
            bottom: 0,
            width: 6,
            cursor: 'col-resize',
            zIndex: 10,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            '&:hover': {
              '& .resize-indicator': {
                opacity: 1,
              },
            },
          }}
        >
          <Box
            className="resize-indicator"
            sx={{
              width: 3,
              height: 40,
              borderRadius: 1,
              bgcolor: 'primary.main',
              opacity: 0.25,
              transition: 'opacity 0.2s',
            }}
          />
        </Box>
        {panelContent}
      </Paper>
      
      {/* Delete Confirmation Dialog */}
      <Dialog open={deleteConfirmOpen} onClose={handleDeleteCancel}>
        <DialogTitle>Delete Expert</DialogTitle>
        <DialogContent>
          <Typography>
            Are you sure you want to delete the expert <strong>{expertListItem?.name}</strong>? This action cannot be undone.
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleDeleteCancel}>Cancel</Button>
          <Button
            onClick={handleDeleteConfirm}
            color="error"
            variant="contained"
            disabled={deleteExpertMutation.isPending}
            startIcon={deleteExpertMutation.isPending ? <CircularProgress size={16} /> : <DeleteIcon />}
          >
            {deleteExpertMutation.isPending ? 'Deleting...' : 'Delete'}
          </Button>
        </DialogActions>
      </Dialog>
      
      {/* Test Expert Dialog */}
      {fullExpert && (
        <TestExpertDialog
          open={testDialogOpen}
          onClose={() => setTestDialogOpen(false)}
          expert={fullExpert}
        />
      )}
    </>
  );
};

export default ExpertEditorPanel;
